import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@cloudgenie/ui';
import { API_BASE, useAuth, hasCap } from '../AuthContext';
import { AdminChrome } from './AdminPage';

/** Wave 7 — user management: list, invite, change role. */

interface UserRow {
  user_id: string;
  email: string;
  name?: string | null;
  role: string;
  org_id?: string | null;
  created_at?: string | null;
  auth_provider?: string | null;
}

const ROLE_OPTIONS = ['owner', 'admin', 'developer', 'viewer', 'billing', 'auditor'];

export default function AdminUsersPage() {
  const { user, loading: authLoading } = useAuth();
  const [rows, setRows] = useState<UserRow[] | null>(null);
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [invEmail, setInvEmail] = useState('');
  const [invName, setInvName] = useState('');
  const [invRole, setInvRole] = useState('viewer');
  const [invBusy, setInvBusy] = useState(false);
  const [invMsg, setInvMsg] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      params.set('limit', '200');
      const r = await fetch(`${API_BASE}/api/admin/users?${params.toString()}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`${r.status}`);
      setRows(await r.json());
    } catch (e) { setError((e as Error).message); }
  }

  useEffect(() => {
    if (authLoading || !user) return;
    if (!hasCap(user, 'admin.ops')) return;
    load();
  }, [authLoading, user]);

  async function invite() {
    if (!invEmail.trim() || !invName.trim()) return;
    setInvBusy(true); setInvMsg(null);
    try {
      const r = await fetch(`${API_BASE}/api/admin/users/invite`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: invEmail.trim(), name: invName.trim(), role: invRole }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(typeof data.detail === 'string' ? data.detail : `${r.status}`);
      setInvMsg(`Invited ${data.email} — password-reset link sent.`);
      setInvEmail(''); setInvName('');
      load();
    } catch (e) { setInvMsg((e as Error).message); }
    finally { setInvBusy(false); }
  }

  async function changeRole(uid: string, newRole: string) {
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/admin/users/${encodeURIComponent(uid)}/role`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : `${r.status}`);
      }
      const updated = await r.json();
      setRows((cur) => (cur ? cur.map((u) => (u.user_id === uid ? { ...u, role: updated.role } : u)) : cur));
    } catch (e) { setError((e as Error).message); }
  }

  if (authLoading) return <AdminChrome title="Users"><p className="text-center py-16 text-muted-foreground">Loading…</p></AdminChrome>;
  if (!user) { window.location.href = `/login?next=${encodeURIComponent('/admin/users')}`; return null; }
  if (!hasCap(user, 'admin.ops')) {
    return <AdminChrome title="Users"><p className="text-center py-16 text-muted-foreground" data-testid="admin-users-forbidden">Missing user.manage capability.</p></AdminChrome>;
  }

  return (
    <AdminChrome title="Users">
      <section className="max-w-6xl mx-auto px-6 pt-10 pb-8" data-testid="admin-users-page">
        <Link to="/admin" className="text-sm text-muted-foreground hover:text-foreground">← Admin</Link>
        <h1 className="text-3xl font-semibold mt-2">Users</h1>

        {/* Invite form */}
        <div className="mt-6 rounded-xl border border-border bg-card p-4" data-testid="invite-user-block">
          <h2 className="text-lg font-semibold mb-3">Invite a user</h2>
          <div className="flex flex-wrap items-center gap-2">
            <input placeholder="email" value={invEmail} onChange={(e) => setInvEmail(e.target.value)}
              className="rounded-md border border-border bg-background px-3 py-2 text-sm flex-1 min-w-[220px]"
              data-testid="invite-email" />
            <input placeholder="name" value={invName} onChange={(e) => setInvName(e.target.value)}
              className="rounded-md border border-border bg-background px-3 py-2 text-sm flex-1 min-w-[180px]"
              data-testid="invite-name" />
            <select value={invRole} onChange={(e) => setInvRole(e.target.value)}
              className="rounded-md border border-border bg-background px-3 py-2 text-sm"
              data-testid="invite-role">
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <Button variant="primary" size="sm" onClick={invite}
              disabled={invBusy || !invEmail.trim() || !invName.trim()}
              data-testid="invite-submit">
              {invBusy ? 'Sending…' : 'Send invite'}
            </Button>
          </div>
          {invMsg && <p className="mt-3 text-sm text-yellow-200" data-testid="invite-msg">{invMsg}</p>}
        </div>

        {/* Search */}
        <div className="mt-8 flex items-center gap-2">
          <input placeholder="Search email or name…" value={q} onChange={(e) => setQ(e.target.value)}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm flex-1 max-w-md"
            data-testid="users-search" />
          <Button variant="outline" size="sm" onClick={load} data-testid="users-refresh">Refresh</Button>
        </div>

        {error && <p role="alert" className="mt-3 text-sm text-red-400" data-testid="users-error">{error}</p>}

        {/* Users table */}
        <div className="mt-6 overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="text-xs uppercase text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Email</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-left px-3 py-2">Org</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows === null && <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">Loading…</td></tr>}
              {rows !== null && rows.length === 0 && <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground">No users match.</td></tr>}
              {rows?.map((u) => (
                <tr key={u.user_id} className="border-t border-border/40" data-testid={`user-row-${u.user_id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{u.email}</td>
                  <td className="px-3 py-2">{u.name || '—'}</td>
                  <td className="px-3 py-2">
                    <select value={u.role} onChange={(e) => changeRole(u.user_id, e.target.value)}
                      className="rounded-md border border-border bg-background px-2 py-1 text-xs"
                      data-testid={`user-role-${u.user_id}`}>
                      {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                      {/* Preserve legacy role labels the DB may currently hold */}
                      {!ROLE_OPTIONS.includes(u.role) && <option value={u.role}>{u.role} (legacy)</option>}
                    </select>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{u.org_id || '—'}</td>
                  <td className="px-3 py-2 text-right text-xs text-muted-foreground">{u.auth_provider || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </AdminChrome>
  );
}
